import { useState, useCallback } from "react";
import { api, uploadSchoolLogo, uploadOrgBanner } from "../services/api";

export function useAccountHandlers({
  user,
  updateUser,
  logout,
  navigate,
  token,
}: {
  user: any;
  updateUser: (u: any) => void;
  logout: () => void;
  navigate: (path: string) => void;
  token: string | null;
}) {
  const [uploadStatus, setUploadStatus] = useState<{
    message: string;
    error?: boolean;
  } | null>(null);
  const [logoError, setLogoError] = useState(false);
  const [isUpdatingName, setIsUpdatingName] = useState(false);
  const [isUpdatingDescription, setIsUpdatingDescription] = useState(false);
  const [isUpdatingType, setIsUpdatingType] = useState(false);
  const [isUpdatingPhoneNumber, setIsUpdatingPhoneNumber] = useState(false);
  const [isUpdatingWebsite, setIsUpdatingWebsite] = useState(false);
  const [isUpdatingPublicPage, setIsUpdatingPublicPage] = useState(false);

  const handleLogoError = useCallback(() => {
    setLogoError(true);
  }, []);

  const uploadLogoFile = useCallback(
    async (file: File) => {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        setUploadStatus({
          message: "Please upload an image file",
          error: true,
        });
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setUploadStatus({
          message: "File size should be less than 5MB",
          error: true,
        });
        return;
      }

      try {
        setUploadStatus({ message: "Uploading..." });
        const schoolId = (user.schoolId as string) || "";

        // Use the updated uploadSchoolLogo function that now calls the new API
        if (!token) {
          throw new Error("Authentication token is required");
        }
        const { logoUrl } = await uploadSchoolLogo(file, schoolId, token);

        // Reset logo error state before updating
        setLogoError(false);

        // Update local user state with the new logo URL
        const updatedUser = { ...user, schoolLogo: logoUrl };
        updateUser(updatedUser);

        setUploadStatus({ message: "Logo updated successfully!" });
        
        // Return the logo URL so the caller can trigger image reload if needed
        return logoUrl;
      } catch {
        setUploadStatus({
          message: "Failed to upload logo. Please try again.",
          error: true,
        });
      }
    },
    [user, updateUser, token]
  );

  const handleLogoUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;
      await uploadLogoFile(file);
    },
    [uploadLogoFile]
  );

  const handleLogoFileUpload = useCallback(
    async (file: File): Promise<boolean> => {
      const result = await uploadLogoFile(file);
      return !!result; // Return true if logoUrl was returned (success), false otherwise
    },
    [uploadLogoFile]
  );

  const uploadBannerFile = useCallback(
    async (base64Image: string) => {
      try {
        setUploadStatus({ message: "Uploading banner..." });
        const schoolId = (user.schoolId as string) || "";

        if (!token) {
          throw new Error("Authentication token is required");
        }
        const { bannerUrl } = await uploadOrgBanner(base64Image, schoolId, token);

        // Update local user state with the new banner URL
        updateUser((current: Record<string, unknown> | null) =>
          current?.schoolId === schoolId
            ? { ...current, orgBanner: bannerUrl }
            : current
        );

        setUploadStatus({ message: "Banner updated successfully!" });
        
        return bannerUrl;
      } catch (error: any) {
        // Provide more specific error message
        let errorMessage = "Failed to upload banner. Please try again.";
        if (error.response) {
          const status = error.response.status;
          const data = error.response.data;
          if (status === 400) {
            errorMessage = data?.message || "Invalid request. Please check the image format.";
          } else if (status === 401) {
            errorMessage = "Authentication failed. Please log in again.";
          } else if (status === 403) {
            errorMessage = "You don't have permission to update the banner.";
          } else if (status === 404) {
            errorMessage = "Organization not found.";
          } else if (status >= 500) {
            errorMessage = "Server error. Please try again later.";
          }
        } else if (error.message) {
          errorMessage = error.message;
        }
        setUploadStatus({
          message: errorMessage,
          error: true,
        });
      }
    },
    [user, updateUser, token]
  );

  const handleBannerFileUpload = useCallback(
    async (base64Image: string): Promise<boolean> => {
      const result = await uploadBannerFile(base64Image);
      return !!result; // Return true if bannerUrl was returned (success), false otherwise
    },
    [uploadBannerFile]
  );

  const handleUpdateSchoolName = useCallback(
    async (newName: string) => {
      if (!newName.trim() || newName.trim() === user.schoolName) {
        return;
      }

      try {
        setIsUpdatingName(true);
        const schoolId = (user.schoolId as string) || "";

        if (!token) {
          throw new Error("Authentication token is required");
        }
        const response = await api.dashboard.updateAccountDetails(
          {
            schoolId,
            name: newName.trim(),
          },
          token
        );

        // Update the user object with the new school name
        await updateUser({ ...user, schoolName: response.school.schoolName });

        setUploadStatus({ message: "School name updated successfully!" });
      } catch (error) {
        setUploadStatus({
          message: "Failed to update school name. Please try again.",
          error: true,
        });
      } finally {
        setIsUpdatingName(false);
      }
    },
    [user, updateUser, token]
  );

  const handleUpdateSchoolDescription = useCallback(
    async (newDescription: string) => {
      if (newDescription.trim() === (user.schoolDescription || "")) {
        return;
      }

      try {
        setIsUpdatingDescription(true);
        const schoolId = (user.schoolId as string) || "";

        if (!token) {
          throw new Error("Authentication token is required");
        }
        const response = await api.dashboard.updateAccountDetails(
          {
            schoolId,
            description: newDescription.trim(),
          },
          token
        );

        // Update the user object with the new school description
        await updateUser({
          ...user,
          schoolDescription: response.school.schoolDescription,
        });

        setUploadStatus({
          message: "School description updated successfully!",
        });
      } catch (error) {
        setUploadStatus({
          message: "Failed to update school description. Please try again.",
          error: true,
        });
      } finally {
        setIsUpdatingDescription(false);
      }
    },
    [user, updateUser, token]
  );

  const handleUpdateSchoolType = useCallback(
    async (newType: string) => {
      if (!newType.trim() || newType.trim() === (user.schoolType || "")) {
        return;
      }

      try {
        setIsUpdatingType(true);
        const schoolId = (user.schoolId as string) || "";

        if (!token) {
          throw new Error("Authentication token is required");
        }
        const response = await api.dashboard.updateAccountDetails(
          {
            schoolId,
            type: newType.trim(),
          },
          token
        );

        // Update the user object with the new school type
        await updateUser({ ...user, schoolType: response.school.schoolType });

        setUploadStatus({ message: "School type updated successfully!" });
      } catch (error) {
        setUploadStatus({
          message: "Failed to update school type. Please try again.",
          error: true,
        });
      } finally {
        setIsUpdatingType(false);
      }
    },
    [user, updateUser, token]
  );

  const handleUpdatePhoneNumber = useCallback(
    async (newPhoneNumber: string) => {
    if (newPhoneNumber.trim() === (user.phoneNumber || "")) {
      return;
    }

    try {
      setIsUpdatingPhoneNumber(true);
      const schoolId = (user.schoolId as string) || "";

      if (!token) {
        throw new Error("Authentication token is required");
      }
      const response = await api.dashboard.updateAccountDetails(
        {
          schoolId,
          phoneNumber: newPhoneNumber.trim() || undefined,
        },
        token
      );

      // Update the user object with the new phone number
      await updateUser({
        ...user,
        phoneNumber: response.school.phoneNumber,
      });

      setUploadStatus({ message: "Phone number updated successfully!" });
    } catch (error) {
      setUploadStatus({
        message: "Failed to update phone number. Please try again.",
        error: true,
      });
    } finally {
      setIsUpdatingPhoneNumber(false);
    }
  },
  [user, updateUser, token]
);

  const handleUpdateWebsite = useCallback(
    async (newWebsite: string) => {
      if (newWebsite.trim() === (user.website || "")) {
        return;
      }

      try {
        setIsUpdatingWebsite(true);
        const schoolId = (user.schoolId as string) || "";

        if (!token) {
          throw new Error("Authentication token is required");
        }
        const response = await api.dashboard.updateAccountDetails(
          {
            schoolId,
            website: newWebsite.trim() || undefined,
          },
          token
        );

        // Update the user object with the new website
        await updateUser({
          ...user,
          website: response.school.website,
        });

        setUploadStatus({ message: "Website updated successfully!" });
      } catch (error) {
        setUploadStatus({
          message: "Failed to update website. Please try again.",
          error: true,
        });
      } finally {
        setIsUpdatingWebsite(false);
      }
    },
    [user, updateUser, token]
  );

  const handleUpdatePublicPage = useCallback(
    async (isPublic: boolean) => {
      try {
        setIsUpdatingPublicPage(true);
        const schoolId = (user.schoolId as string) || "";

        if (!token) {
          throw new Error("Authentication token is required");
        }

        const response = await api.dashboard.updateAccountDetails(
          {
            schoolId,
            isPublic,
          },
          token
        );

        const updatedIsPublic = response?.organization?.isPublic ?? (isPublic ? "true" : "false");

        if (!response?.organization) {
          throw new Error("Invalid response from server: missing organization data");
        }

        if (response.organization.isPublic === undefined) {
          throw new Error("Invalid response from server: isPublic not returned");
        }

        updateUser((prevUser: Record<string, unknown> | null): Record<string, unknown> | null => {
          if (!prevUser) {
            return prevUser;
          }

          return {
            ...prevUser,
            isPublic: updatedIsPublic,
          };
        });

        setUploadStatus({ message: "Public page setting updated successfully!" });
      } catch (error: any) {
        setUploadStatus({
          message: error?.response?.data?.error || error?.message || "Failed to update public page setting. Please try again.",
          error: true,
        });
        // Re-throw error so the component can handle it
        throw error;
      } finally {
        setIsUpdatingPublicPage(false);
      }
    },
    [user, updateUser, token]
  );

  const [isUpdatingShowContactInfo, setIsUpdatingShowContactInfo] = useState(false);
  const [isUpdatingPublicPageSettings, setIsUpdatingPublicPageSettings] = useState(false);
  const [isUpdatingHidePhoneInApp, setIsUpdatingHidePhoneInApp] = useState(false);

  const handleUpdateShowContactInfo = useCallback(
    async (showContactInfo: boolean) => {
      try {
        setIsUpdatingShowContactInfo(true);
        const schoolId = (user.schoolId as string) || "";

        if (!token) {
          throw new Error("Authentication token is required");
        }
        await api.dashboard.updateAccountDetails(
          {
            schoolId,
            showContactInfo,
          },
          token
        );

        // Update the user object with the new showContactInfo value
        await updateUser({
          ...user,
          showContactInfo: showContactInfo ? "true" : "false",
        });

        setUploadStatus({ message: "Contact info visibility updated successfully!" });
      } catch (error) {
        setUploadStatus({
          message: "Failed to update contact info visibility. Please try again.",
          error: true,
        });
      } finally {
        setIsUpdatingShowContactInfo(false);
      }
    },
    [user, updateUser, token]
  );

  const handleUpdateHidePhoneInApp = useCallback(
    async (hidePhoneInApp: boolean) => {
      const current = user?.hidePhoneInApp === true;
      if (hidePhoneInApp === current) {
        return;
      }

      try {
        setIsUpdatingHidePhoneInApp(true);
        const schoolId = (user.schoolId as string) || "";

        if (!token) {
          throw new Error("Authentication token is required");
        }

        const response = await api.dashboard.updateAccountDetails(
          { schoolId, hidePhoneInApp },
          token
        );

        await updateUser({
          ...user,
          hidePhoneInApp:
            response.organization?.hidePhoneInApp ?? hidePhoneInApp,
        });

        setUploadStatus({
          message: hidePhoneInApp
            ? "Phone number will be hidden from the mobile app."
            : "Phone number can appear in the mobile app again.",
        });
      } catch {
        setUploadStatus({
          message:
            "Failed to update mobile visibility for your phone number. Please try again.",
          error: true,
        });
      } finally {
        setIsUpdatingHidePhoneInApp(false);
      }
    },
    [user, updateUser, token]
  );

  const handleUpdatePublicPageSettings = useCallback(
    async (publicPageSettings: any) => {
      try {
        setIsUpdatingPublicPageSettings(true);
        const schoolId = (user.schoolId as string) || "";

        if (!token) {
          throw new Error("Authentication token is required");
        }
        
        const response = await api.dashboard.updateAccountDetails(
          {
            schoolId,
            publicPageSettings,
          },
          token
        );
        
        // Update the user object with the new publicPageSettings
        // The API returns the updated settings in the response
        await updateUser({
          ...user,
          publicPageSettings: response.organization?.publicPageSettings || publicPageSettings,
        });

        // Don't show status message for every toggle change to avoid spam
      } catch (error) {
        setUploadStatus({
          message: "Failed to update public page settings. Please try again.",
          error: true,
        });
      } finally {
        setIsUpdatingPublicPageSettings(false);
      }
    },
    [user, updateUser, token]
  );

  const handleLogout = useCallback(() => {
    logout();
    navigate("/login");
    setUploadStatus(null);
  }, [logout, navigate]);

  const handleForgotPassword = useCallback(() => {
    navigate("/forgot-password");
  }, [navigate]);

  const clearUploadStatus = useCallback(() => {
    setUploadStatus(null);
  }, []);

  return {
    handleLogoUpload,
    handleLogoFileUpload,
    handleBannerFileUpload,
    handleLogoError,
    handleUpdateSchoolName,
    handleUpdateSchoolDescription,
    handleUpdateSchoolType,
    handleUpdatePhoneNumber,
    handleUpdateWebsite,
    handleUpdatePublicPage,
    handleLogout,
    handleForgotPassword,
    uploadStatus,
    logoError,
    setUploadStatus,
    clearUploadStatus,
    isUpdatingName,
    isUpdatingDescription,
    isUpdatingType,
    isUpdatingPhoneNumber,
    isUpdatingWebsite,
    isUpdatingPublicPage,
    handleUpdateShowContactInfo,
    isUpdatingShowContactInfo,
    handleUpdatePublicPageSettings,
    isUpdatingPublicPageSettings,
    handleUpdateHidePhoneInApp,
    isUpdatingHidePhoneInApp,
  };
}
